import React, { useState, useEffect } from 'react';
import { useBrandingColors } from '../hooks/useBrandingColors';
import {
  UserIcon,
  BriefcaseIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { getVacancyResponses, evaluateResume } from './services/api';
import { ManagementPagination } from '../components/ui/ManagementPagination';
import { formatCandidateShortName } from './utils/candidatePrivacy';
import toast from 'react-hot-toast';

const VacancyResponses = ({ vacancyId, onClose }) => {
  const colors = useBrandingColors();
  const [loading, setLoading] = useState(true);
  const [vacancy, setVacancy] = useState(null);
  const [responses, setResponses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResponses, setTotalResponses] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [aiEvaluations, setAiEvaluations] = useState({});
  const [evaluatingIds, setEvaluatingIds] = useState(new Set());

  useEffect(() => {
    fetchResponses();
  }, [vacancyId, currentPage]);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const response = await getVacancyResponses(vacancyId, currentPage, itemsPerPage);

      setVacancy(response.vacancy);
      setResponses(response.responses.items || []);
      setTotalPages(response.responses.pages || 1);
      setTotalResponses(response.responses.found || 0);
      setItemsPerPage(response.responses.per_page || itemsPerPage);
      
      // Automatically start AI evaluation for new responses
      response.responses.items.forEach(item => {
        if (item.resume && !aiEvaluations[item.id]) {
          evaluateResumeForItem(item.id, item.resume);
        }
      });
    } catch (error) {
      console.error('Error fetching responses:', error);
      toast.error('Ошибка при загрузке откликов');
    } finally {
      setLoading(false);
    }
  };

  const evaluateResumeForItem = async (responseId, resumeData) => {
    // Skip if already evaluating or evaluated
    if (evaluatingIds.has(responseId) || aiEvaluations[responseId]) {
      return;
    }

    setEvaluatingIds(prev => new Set(prev).add(responseId));

    try {
      const response = await evaluateResume(vacancyId, resumeData);
      
      setAiEvaluations(prev => ({
        ...prev,
        [responseId]: response
      }));
    } catch (error) {
      console.error('Error evaluating resume:', error);
      setAiEvaluations(prev => ({
        ...prev,
        [responseId]: { rating: 0, comment: 'Ошибка оценки' }
      }));
    } finally {
      setEvaluatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(responseId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSalary = (salary) => {
    if (!salary) return 'Не указана';
    
    let result = '';
    if (salary.from) result += `от ${salary.from.toLocaleString('ru-RU')}`;
    if (salary.to) result += `${result ? ' ' : ''}до ${salary.to.toLocaleString('ru-RU')}`;
    if (salary.currency) result += ` ${salary.currency}`;
    
    return result || 'Не указана';
  };

  const getStateColor = (state) => {
    const stateId = state?.id || state;
    switch (stateId) {
      case 'invitation': return 'bg-green-100 text-green-800';
      case 'discard': return 'bg-red-100 text-red-800';
      case 'response': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 80) return 'text-green-600';
    if (rating >= 60) return 'text-yellow-600';
    if (rating >= 40) return 'text-white';
    return 'text-red-600';
  };

  const RatingStars = ({ rating }) => {
    const stars = Math.round(rating / 20); // Convert 0-100 to 0-5 stars
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          i < stars ? (
            <StarSolidIcon key={i} className="h-4 w-4 text-yellow-400" />
          ) : (
            <StarIcon key={i} className="h-4 w-4 text-gray-300" />
          )
        ))}
        <span className={`ml-2 font-semibold ${getRatingColor(rating)}`}
              style={rating >= 40 && rating < 60 ? { backgroundColor: colors.primary, padding: '0 6px', borderRadius: '8px' } : undefined}
        >
          {rating}%
        </span>
      </div>
    );
  };

  if (loading && responses.length === 0) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6">
          <div className="inline-flex items-center">
            <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Загрузка откликов...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Отклики на вакансию: {vacancy?.name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Всего откликов: {totalResponses}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Show counters if available */}
          {vacancy?.counters && (
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-xl p-3">
                <div className="text-sm text-blue-600">Всего откликов</div>
                <div className="text-2xl font-bold text-blue-900">{vacancy.counters.responses || 0}</div>
              </div>
              <div className="bg-green-50 rounded-xl p-3">
                <div className="text-sm text-green-600">Приглашения</div>
                <div className="text-2xl font-bold text-green-900">{vacancy.counters.invitations || 0}</div>
              </div>
              <div className="bg-yellow-50 rounded-xl p-3">
                <div className="text-sm text-yellow-600">В работе</div>
                <div className="text-2xl font-bold text-yellow-900">{vacancy.counters.resumes_in_progress || 0}</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-3">
                <div className="text-sm text-purple-600">Непрочитанные</div>
                <div className="text-2xl font-bold text-purple-900">{vacancy.counters.unread_responses || 0}</div>
              </div>
            </div>
          )}

          {responses.length > 0 ? (
            <div className="space-y-4">
              {responses.map((response) => {
                const evaluation = aiEvaluations[response.id];
                const isEvaluating = evaluatingIds.has(response.id);
                
                return (
                  <div key={response.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {formatCandidateShortName({
                              first_name: response.resume?.first_name,
                              last_name: response.resume?.last_name,
                              middle_name: response.resume?.middle_name,
                              fullName: response.resume?.full_name,
                              name: response.resume?.name,
                            }, response.resume?.title || 'Без названия')}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStateColor(response.state)}`}>
                            {response.state?.name || 'Новый'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 mb-3">
                          {response.resume?.total_experience && (
                            <div className="flex items-center">
                              <BriefcaseIcon className="h-4 w-4 mr-1" />
                              Опыт: {Math.floor(response.resume.total_experience.months / 12)} лет
                            </div>
                          )}
                          {response.resume?.area && (
                            <div className="flex items-center">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {response.resume.area.name}
                            </div>
                          )}
                          {response.resume?.salary && (
                            <div className="flex items-center">
                              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                              {formatSalary(response.resume.salary)}
                            </div>
                          )}
                          {response.created_at && (
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {formatDate(response.created_at)}
                            </div>
                          )}
                        </div>

                        {/* AI Evaluation */}
                        <div className="bg-gray-50 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Оценка соответствия:</span>
                            {isEvaluating ? (
                              <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Анализ...
                              </div>
                            ) : evaluation ? (
                              <RatingStars rating={evaluation.rating} />
                            ) : (
                              <span className="text-sm text-gray-400">Ожидание анализа</span>
                            )}
                          </div>
                          {evaluation?.comment && (
                            <p className="text-sm text-gray-600">{evaluation.comment}</p>
                          )}
                        </div>

                        {/* Skills */}
                        {response.resume?.skill_set && response.resume.skill_set.length > 0 && (
                          <div className="mt-3">
                            <div className="flex flex-wrap gap-1">
                              {response.resume.skill_set.slice(0, 10).map((skill, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {skill.name || skill}
                                </span>
                              ))}
                              {response.resume.skill_set.length > 10 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                  +{response.resume.skill_set.length - 10}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 ml-4">
                        {response.has_updates && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                            Есть обновления
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Пока нет откликов на эту вакансию
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6">
              <ManagementPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalResponses}
                itemsPerPage={itemsPerPage}
                onPageChange={(p) => setCurrentPage(p)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacancyResponses;
